import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../api/apiService';
import GitlabMarkdownRenderer from './GitlabMarkdownRenderer';
import { useNotification } from '../context/NotificationContext';
import { useTaskPolling } from '../context/TaskPollingContext';

const ExtractFeatures = () => {
  const { addToast } = useNotification();
  const { registerTask } = useTaskPolling();
  // Auto-resize textarea function
  const autoResizeTextarea = (textareaRef) => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState(''); // 'codebase' or 'document'
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [featuresExtracted, setFeaturesExtracted] = useState(false);
  const [extractionAttempted, setExtractionAttempted] = useState(false);
  const [hasDetailedFeatures, setHasDetailedFeatures] = useState(false);
  const [extractionStarted, setExtractionStarted] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [showAddFeatureForm, setShowAddFeatureForm] = useState(false);
  const [showAddSubfeatureForm, setShowAddSubfeatureForm] = useState(null);
  const [showEditFeatureForm, setShowEditFeatureForm] = useState(null);
  const [showEditSubfeatureForm, setShowEditSubfeatureForm] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, type: null, id: null, name: '' });
  const [newFeature, setNewFeature] = useState({ title: '', description: '' });
  const newFeatureDescRef = useRef(null);
  const [newSubfeature, setNewSubfeature] = useState({ title: '', description: '' });
  const newSubfeatureDescRef = useRef(null);
  const [editFeature, setEditFeature] = useState({ title: '', description: '' });
  const editFeatureDescRef = useRef(null);
  const [editSubfeature, setEditSubfeature] = useState({ title: '', description: '' });
  const editSubfeatureDescRef = useRef(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Watch for task completion to reload features
  useEffect(() => {
    if (!activeTaskId) return;

    const checkCompletion = setInterval(async () => {
      try {
        const task = await apiService.getTaskStatus(activeTaskId);
        
        if (task.status === 'completed') {
          clearInterval(checkCompletion);
          setActiveTaskId(null);
          setExtractionStarted(false);
          // Reload detailed features from DB
          try {
            const result = await apiService.getStoredFeaturesWithDetails(selectedProject);
            setFeatures(result || []);
            setHasDetailedFeatures(true);
            setFeaturesExtracted(true);
          } catch (e) {
            console.error('Failed to reload features after completion:', e);
          }
        } else if (task.status === 'failed') {
          clearInterval(checkCompletion);
          setActiveTaskId(null);
          setExtractionStarted(false);
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 3000);

    return () => {
      clearInterval(checkCompletion);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId, selectedProject]);

  // Auto-resize textareas when they appear
  useEffect(() => {
    if (showEditFeatureForm) {
      setTimeout(() => autoResizeTextarea(editFeatureDescRef), 0);
    }
  }, [showEditFeatureForm]);

  useEffect(() => {
    if (showEditSubfeatureForm) {
      setTimeout(() => autoResizeTextarea(editSubfeatureDescRef), 0);
    }
  }, [showEditSubfeatureForm]);

  useEffect(() => {
    if (showAddFeatureForm) {
      setTimeout(() => autoResizeTextarea(newFeatureDescRef), 0);
    }
  }, [showAddFeatureForm]);

  useEffect(() => {
    if (showAddSubfeatureForm) {
      setTimeout(() => autoResizeTextarea(newSubfeatureDescRef), 0);
    }
  }, [showAddSubfeatureForm]);

  const fetchProjects = async () => {
    try {
      const projectList = await apiService.getProjects();
      setProjects(projectList);
    } catch (err) {
      setError('Failed to fetch projects: ' + err.message);
    }
  };


  const handleProjectChange = async (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);

    // Find the project in projects
    const selectedProjectObj = projects.find(p => p.id === projectId);
    if (selectedProjectObj) {
      setProjectName(selectedProjectObj.name);
      setProjectType(selectedProjectObj.type);
    } else {
      // If project is not found, clear the project name and type
      setProjectName('');
      setProjectType('');
    }

    // Reset features extracted state when project changes
    setFeaturesExtracted(false);
    setExtractionAttempted(false); // Reset extraction attempted state when project changes
    setHasDetailedFeatures(false); // Reset detailed features status when project changes

    if (projectId) {
      try {
        setLoading(true);
        let featuresData = [];

        // Fetch features for project (works for both codebase and document projects)
        featuresData = await apiService.getFeaturesByProject(projectId);

        setFeatures(featuresData || []);

        // Check if detailed features exist for this project
        // This check now works for both codebase and document projects
        try {
          const detailedCheck = await apiService.getHasDetailedExtraction(projectId);
          setHasDetailedFeatures(detailedCheck.has_detailed_features);
        } catch (err) {
          console.error('Error checking detailed extraction:', err);
          // Set to false if the call fails
          setHasDetailedFeatures(false);
        }
      } catch (err) {
        setError('Failed to fetch features: ' + err.message);
        setFeatures([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExtractFeatures = async () => {
    if (!selectedProject || !projectName) return;

    try {
      setLoading(true);

      // Fetch existing features from the database for the selected project
      let featuresData = [];
      featuresData = await apiService.getFeaturesByProject(selectedProject);

      setFeatures(featuresData || []);
      setFeaturesExtracted(featuresData && featuresData.length > 0); // Mark that features have been extracted if there are features
      setExtractionAttempted(true); // Mark that extraction has been attempted

      // Check if detailed features exist for this project
      // This check now works for both codebase and document projects
      try {
        const detailedCheck = await apiService.getHasDetailedExtraction(selectedProject);
        setHasDetailedFeatures(detailedCheck.has_detailed_features);
      } catch (err) {
        console.error('Error checking detailed extraction:', err);
        // Set to false if the call fails
        setHasDetailedFeatures(false);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch features: ' + err.message);
      setExtractionAttempted(true); // Still mark extraction as attempted even if it failed
    } finally {
      setLoading(false);
    }
  };

  const handleExtractDetails = async () => {
    if (!selectedProject || !projectName) return;

    try {
      setLoading(true);
      setError(null);

      // Call /api/has_detailed_extraction/{project_id} to check if detailed features exist
      const detailedCheck = await apiService.getHasDetailedExtraction(selectedProject);

      if (detailedCheck.has_detailed_features) {
        // If has_detailed_features is true, call /api/stored_features_with_details/{project_id}
        const result = await apiService.getStoredFeaturesWithDetails(selectedProject);
        setFeatures(result || []);
        setHasDetailedFeatures(true);
      } else {
        // If has_detailed_features is false, call background extraction
        const response = await apiService.extractFeatures(selectedProject, projectName);

        // Store task_id and start polling
        const tid = response.task_id;
        setActiveTaskId(tid);
        setExtractionStarted(true);

        // Create toast notification
        const newToastId = addToast({
          title: projectName,
          message: 'Starting feature extraction...',
          type: 'info',
          status: 'running',
        });

        // Register with global polling service
        registerTask(tid, newToastId, projectName, 'feature_detail_extraction');

        setLoading(false);
        return; // polling takes over from here
      }

      // Update the status to reflect the current state
      setHasDetailedFeatures(true);
    } catch (err) {
      console.error('Extraction error:', err);
      setError('Extraction failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = async () => {
    if (!newFeature.title || !selectedProject) return;

    try {
      const featureData = {
        project_id: selectedProject,
        project_name: projectName,
        feature_title: newFeature.title,
        description: newFeature.description || ''
      };

      let newFeatureResult;
      if (projectType === 'document') {
        // For documents, we might need a different API call
        // For now, we'll use the same API but may need to update this later
        newFeatureResult = await apiService.addFeature(featureData);
      } else {
        // For codebase projects
        newFeatureResult = await apiService.addFeature(featureData);
      }

      // Add to local state
      setFeatures([...features, { ...newFeatureResult, subfeatures: [] }]);
      setNewFeature({ title: '' });
      setShowAddFeatureForm(false);

      // Update the extracted state to show the features list
      setFeaturesExtracted(true);
      setExtractionAttempted(true);
    } catch (err) {
      setError('Failed to add feature: ' + err.message);
    }
  };

  const handleUpdateFeature = async (featureId) => {
    if (!editFeature.title) return;

    try {
      const featureData = {
        feature_title: editFeature.title,
        description: editFeature.description
      };

      const updatedFeature = await apiService.updateFeature(featureId, featureData);

      // Update in local state
      setFeatures(features.map(f =>
        f.id === featureId ? { ...f, ...updatedFeature } : f
      ));
      setShowEditFeatureForm(null);
      setEditFeature({ title: '' });
    } catch (err) {
      setError('Failed to update feature: ' + err.message);
    }
  };

  const handleDeleteFeature = async (featureId, featureName) => {
    setDeleteConfirmation({
      show: true,
      type: 'feature',
      id: featureId,
      name: featureName || 'this feature'
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id) return;

    try {
      if (deleteConfirmation.type === 'feature') {
        await apiService.deleteFeature(deleteConfirmation.id);
        setFeatures(features.filter(f => f.id !== deleteConfirmation.id));
      } else if (deleteConfirmation.type === 'subfeature') {
        await apiService.deleteSubfeature(deleteConfirmation.id);
        setFeatures(features.map(f =>
          f.id === deleteConfirmation.featureId
            ? { ...f, subfeatures: f.subfeatures.filter(sf => sf.id !== deleteConfirmation.id) }
            : f
        ));
      }
    } catch (err) {
      setError('Failed to delete ' + deleteConfirmation.type + ': ' + err.message);
    } finally {
      setDeleteConfirmation({ show: false, type: null, id: null, name: '' });
    }
  };

  const handleAddSubfeature = async (featureId) => {
    if (!newSubfeature.title) return;

    try {
      const subfeatureData = {
        feature_id: featureId,
        project_id: selectedProject,
        project_name: projectName,
        subfeature_title: newSubfeature.title,
        description: newSubfeature.description || ''
      };

      let newSubfeatureResult;
      if (projectType === 'document') {
        // For documents, we might need a different API call
        // For now, we'll use the same API but may need to update this later
        newSubfeatureResult = await apiService.addSubfeature(subfeatureData);
      } else {
        // For codebase projects
        newSubfeatureResult = await apiService.addSubfeature(subfeatureData);
      }

      // Update the feature in local state to include the new subfeature
      setFeatures(features.map(f =>
        f.id === featureId
          ? { ...f, subfeatures: [...f.subfeatures, newSubfeatureResult] }
          : f
      ));
      setNewSubfeature({ title: '' });
      setShowAddSubfeatureForm(null);
    } catch (err) {
      setError('Failed to add subfeature: ' + err.message);
    }
  };

  const handleUpdateSubfeature = async (featureId, subfeatureId) => {
    if (!editSubfeature.title) return;

    try {
      const subfeatureData = {
        subfeature_title: editSubfeature.title,
        description: editSubfeature.description
      };

      const updatedSubfeature = await apiService.updateSubfeature(subfeatureId, subfeatureData);

      // Update in local state
      setFeatures(features.map(f =>
        f.id === featureId
          ? {
            ...f,
            subfeatures: f.subfeatures.map(sf =>
              sf.id === subfeatureId ? { ...sf, ...updatedSubfeature } : sf
            )
          }
          : f
      ));
      setShowEditSubfeatureForm(null);
      setEditSubfeature({ title: '' });
    } catch (err) {
      setError('Failed to update subfeature: ' + err.message);
    }
  };

  const handleDeleteSubfeature = async (featureId, subfeatureId, subfeatureName) => {
    setDeleteConfirmation({
      show: true,
      type: 'subfeature',
      id: subfeatureId,
      featureId: featureId, // Store featureId for updating state later
      name: subfeatureName || 'this subfeature'
    });
  };

  const handleDownloadFeatures = () => {
    const dataStr = JSON.stringify(features, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const fileName = projectName ? projectName.replace(' ', '_') : 'project';
    const exportFileDefaultName = `${fileName}_features.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Extract Features</h2>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-blue-700">Extract structured features and sub-features from your processed codebase or document projects.</p>
      </div>

      <div className="mb-6">
        <label htmlFor="projectSelect" className="block text-sm font-medium text-gray-700 mb-2">
          Select Project
        </label>
        <select
          id="projectSelect"
          value={selectedProject}
          onChange={handleProjectChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select a project</option>
          {projects.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <button
          onClick={handleExtractFeatures}
          disabled={!selectedProject || loading || featuresExtracted}
          className={`py-2 px-4 rounded-md text-white font-medium ${!selectedProject || loading || featuresExtracted
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          {loading ? 'Extracting...' : featuresExtracted ? 'Features Extracted' : 'Extract Features'}
        </button>

        {/* Inline status display while processing */}
        {extractionStarted && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <p className="text-sm font-medium text-blue-800">
                Extraction running in background...
              </p>
            </div>
            <p className="text-xs text-blue-600">
              Check the top-right corner for live status updates. Claude is analyzing your codebase.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {featuresExtracted && features.length > 0 ? (
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-4">Extracted Features/Subfeatures ({features.length} total)</h3>

          <div className="space-y-4">
            {features.map((feature, i) => (
              <div key={feature.id || i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  {showEditFeatureForm === feature.id ? (
                    <input
                      type="text"
                      value={editFeature.title}
                      onChange={(e) => setEditFeature({ ...editFeature, title: e.target.value })}
                      className="px-2 py-1 text-lg font-medium text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 flex-grow mr-2"
                    />
                  ) : (
                    <h4 className="text-lg font-medium text-gray-900">
                      {feature.feature_title || feature.name || 'Untitled Feature'}
                    </h4>
                  )}
                  <div className="flex space-x-2">
                    {showEditFeatureForm === feature.id ? (
                      <>
                        <button
                          onClick={() => handleUpdateFeature(feature.id)}
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setShowEditFeatureForm(null);
                            setEditFeature({ title: feature.feature_title || feature.name || '', description: feature.description || '' });
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setEditFeature({
                            title: feature.feature_title || feature.name || '',
                            description: feature.description || ''
                          });
                          setShowEditFeatureForm(feature.id);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteFeature(feature.id, feature.feature_title || feature.name || 'Untitled Feature')}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {showEditFeatureForm === feature.id ? (
                  <div className="mt-2">
                    <textarea
                      ref={editFeatureDescRef}
                      value={editFeature.description}
                      onChange={(e) => {
                        setEditFeature({ ...editFeature, description: e.target.value });
                        autoResizeTextarea(editFeatureDescRef);
                      }}
                      placeholder="Feature Description (supports Markdown)"
                      className="w-full px-2 py-1 text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
                      rows="1"
                      style={{ minHeight: '40px' }}
                    />
                  </div>
                ) : feature.description && (
                  <div className="mt-2 text-gray-700">
                    <strong>Description:</strong>
                    <div className="ml-2 mt-1">
                      <GitlabMarkdownRenderer content={feature.description} />
                    </div>
                  </div>
                )}

                {feature.subfeatures && feature.subfeatures.length > 0 && (
                  <div className="mt-4 ml-4">
                    <h5 className="font-medium text-gray-800 mb-2">Sub-features:</h5>
                    <div className="space-y-3">
                      {feature.subfeatures.map((subfeature, j) => (
                        <div key={subfeature.id || j} className="border-l-2 border-gray-200 pl-3 py-1">
                          <div className="flex justify-between items-start">
                            {showEditSubfeatureForm &&
                              showEditSubfeatureForm.featureId === feature.id &&
                              showEditSubfeatureForm.subfeatureId === subfeature.id ? (
                              <input
                                type="text"
                                value={editSubfeature.title}
                                onChange={(e) => setEditSubfeature({ ...editSubfeature, title: e.target.value })}
                                className="px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-600 flex-grow mr-2"
                              />
                            ) : (
                              <span className="text-gray-700">
                                {subfeature.subfeature_title || subfeature.name || 'Untitled Sub-feature'}
                              </span>
                            )}
                            <div className="flex space-x-2">
                              {showEditSubfeatureForm &&
                                showEditSubfeatureForm.featureId === feature.id &&
                                showEditSubfeatureForm.subfeatureId === subfeature.id ? (
                                <>
                                  <button
                                    onClick={() => handleUpdateSubfeature(feature.id, subfeature.id)}
                                    className="text-xs text-green-600 hover:text-green-800"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowEditSubfeatureForm(null);
                                      setEditSubfeature({ title: subfeature.subfeature_title || subfeature.name || '', description: subfeature.description || '' });
                                    }}
                                    className="text-xs text-gray-600 hover:text-gray-800"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditSubfeature({
                                      title: subfeature.subfeature_title || subfeature.name || '',
                                      description: subfeature.description || ''
                                    });
                                    setShowEditSubfeatureForm({ featureId: feature.id, subfeatureId: subfeature.id });
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteSubfeature(feature.id, subfeature.id, subfeature.subfeature_title || subfeature.name || 'Untitled Sub-feature')}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          {showEditSubfeatureForm &&
                            showEditSubfeatureForm.featureId === feature.id &&
                            showEditSubfeatureForm.subfeatureId === subfeature.id ? (
                            <div className="mt-1">
                              <textarea
                                ref={editSubfeatureDescRef}
                                value={editSubfeature.description}
                                onChange={(e) => {
                                  setEditSubfeature({ ...editSubfeature, description: e.target.value });
                                  autoResizeTextarea(editSubfeatureDescRef);
                                }}
                                placeholder="Subfeature Description (supports Markdown)"
                                className="w-full px-2 py-1 text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-600 resize-none overflow-hidden"
                                rows="1"
                                style={{ minHeight: '30px' }}
                              />
                            </div>
                          ) : subfeature.description && (
                            <div className="mt-1 text-sm text-gray-600">
                              <strong>Description:</strong>
                              <div className="ml-2 mt-1">
                                <GitlabMarkdownRenderer content={subfeature.description} />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {featuresExtracted && (
                  <div className="mt-3 ml-4">
                    {!hasDetailedFeatures ? (
                      <button
                        onClick={() => setShowAddSubfeatureForm(feature.id)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Subfeature
                      </button>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        Detailed features have been extracted. Manual subfeature addition is disabled.
                      </div>
                    )}
                  </div>
                )}

                {showAddSubfeatureForm === feature.id && featuresExtracted && !hasDetailedFeatures && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium text-gray-900">Add New Subfeature</h3>
                          <button
                            onClick={() => setShowAddSubfeatureForm(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <span className="text-2xl">&times;</span>
                          </button>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-4">
                            <input
                              type="text"
                              value={newSubfeature.title}
                              onChange={(e) => setNewSubfeature({ ...newSubfeature, title: e.target.value })}
                              placeholder="Subfeature Title"
                              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                            <textarea
                              ref={newSubfeatureDescRef}
                              value={newSubfeature.description}
                              onChange={(e) => {
                                setNewSubfeature({ ...newSubfeature, description: e.target.value });
                                autoResizeTextarea(newSubfeatureDescRef);
                              }}
                              placeholder="Subfeature Description (supports Markdown)"
                              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
                              rows="1"
                              style={{ minHeight: '40px' }}
                            />
                          </div>
                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => handleAddSubfeature(feature.id)}
                              className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                              Add Subfeature
                            </button>
                            <button
                              onClick={() => setShowAddSubfeatureForm(null)}
                              className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showAddSubfeatureForm === feature.id && featuresExtracted && hasDetailedFeatures && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium text-gray-900">Add New Subfeature</h3>
                          <button
                            onClick={() => setShowAddSubfeatureForm(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <span className="text-2xl">&times;</span>
                          </button>
                        </div>
                        <div className="p-4">
                          <div className="text-red-600 mb-4">
                            Detailed features have been extracted. Manual subfeature addition is disabled.
                          </div>
                          <div className="flex justify-end">
                            <button
                              onClick={() => setShowAddSubfeatureForm(null)}
                              className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="mt-4">
              {!hasDetailedFeatures ? (
                <button
                  onClick={() => setShowAddFeatureForm(true)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  + Add New Feature
                </button>
              ) : (
                <div className="text-gray-500 text-sm">
                  Detailed features have been extracted. Manual feature addition is disabled.
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={handleExtractDetails}
              disabled={!selectedProject || loading || hasDetailedFeatures || !!activeTaskId}
              className={`py-2 px-4 rounded-md text-white font-medium ${!selectedProject || loading || hasDetailedFeatures || !!activeTaskId
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                }`}
            >
              {activeTaskId ? 'Extracting Details...' : loading ? 'Processing...' : hasDetailedFeatures ? 'Details Extracted' : 'Extract Details'}
            </button>
            <button
              onClick={handleDownloadFeatures}
              className="py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Download Feature JSON
            </button>
          </div>
        </div>
      ) : (
        // Empty state when no features are extracted
        <div className="mb-6">
          {selectedProject && extractionAttempted ? (
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Features Found</h3>
              {!hasDetailedFeatures ? (
                <>
                  <p className="text-gray-500 mb-6">No features were extracted from the selected project. You can add features manually.</p>
                  <button
                    onClick={() => setShowAddFeatureForm(true)}
                    className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    + Add New Feature
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-500 mb-6">Detailed features have been extracted. Manual feature addition is disabled.</p>
                </>
              )}
            </div>
          ) : (
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <div className="text-gray-500 text-5xl mb-4">📁</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Project and Extract Features</h3>
              <p className="text-gray-500 mb-6">Please select a project from the dropdown above and click the 'Extract Features' button to analyze the codebase.</p>
            </div>
          )}
        </div>
      )}

      {/* Add Feature Modal */}
      {showAddFeatureForm && !hasDetailedFeatures && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Feature</h3>
                <button
                  onClick={() => setShowAddFeatureForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  <input
                    type="text"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature({ ...newFeature, title: e.target.value })}
                    placeholder="Feature Title"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    ref={newFeatureDescRef}
                    value={newFeature.description}
                    onChange={(e) => {
                      setNewFeature({ ...newFeature, description: e.target.value });
                      autoResizeTextarea(newFeatureDescRef);
                    }}
                    placeholder="Feature Description (supports Markdown)"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
                    rows="1"
                    style={{ minHeight: '40px' }}
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleAddFeature}
                    className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Feature
                  </button>
                  <button
                    onClick={() => setShowAddFeatureForm(false)}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddFeatureForm && hasDetailedFeatures && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Feature</h3>
                <button
                  onClick={() => setShowAddFeatureForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <div className="text-red-600 mb-4">
                  Detailed features have been extracted. Manual feature addition is disabled.
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddFeatureForm(false)}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirmation({ show: false, type: null, id: null, name: '' })}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-700">
                  Are you sure you want to delete <strong>{deleteConfirmation.name}</strong>?
                  This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => setDeleteConfirmation({ show: false, type: null, id: null, name: '' })}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractFeatures;